import { Card, Typography } from 'antd';
import { Project } from '@common/data/projects';
import { NavigateFunction } from 'react-router-dom';

const { Title, Paragraph } = Typography;

export interface ProjectCardProps {
  project: Project;
  navigate: NavigateFunction;
}

export const ProjectCard = ({ project, navigate }: ProjectCardProps) => {
  // Helper functions to handle both new and legacy field names
  const getProjectDisplayName = () => {
    return project.name || project.title || 'Untitled Project';
  };

  const getProjectImage = () => {
    const imageUrl = import.meta.env.VITE_URL_FILES + '/' + project.image;
    return imageUrl || '/assets/images/project-default.jpg';
  };

  const getProjectDescription = () => {
    return project.description || 'No description available';
  };

  const getProjectInfo = () => {
    const info = [];
    
    if (project.duration) {
      info.push(`Duration: ${project.duration}`);
    }
    
    if (project.siteArea) {
      info.push(`Site: ${project.siteArea}`);
    }
    
    if (project.earthMoved && project.earthMoved !== 'N/A') {
      info.push(`Earth: ${project.earthMoved}`);
    }
    
    // Fallback to legacy services if available
    if (info.length === 0 && project.services && Array.isArray(project.services)) {
      return project.services.join(' • ');
    }
    
    return info.length > 0 ? info.join(' • ') : 'Project Details';
  };

  const isHighlighted = () => {
    return project.isHighlight || project.featured || false;
  };

  return (
    <Card 
      className="bg-first-dark-gray !border-none cursor-pointer hover:opacity-90 transition-opacity relative h-[500px] flex flex-col"
      bodyStyle={{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column' }}
      onClick={() => { navigate(`/projects/${project.id}`); window.scrollTo(0, 0); }}
    >
      {isHighlighted() && (
        <div className="absolute top-2 left-2 z-10 bg-orangicide text-black border-none font-['Barlow'] font-semibold px-2 py-1 text-xs">
          HIGHLIGHTED
        </div>
      )}
      
      <div className="h-50 w-full flex-shrink-0">
        <img 
          src={getProjectImage()} 
          alt={getProjectDisplayName()} 
          className="w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.src = project.placeholderImage || 'https://placehold.co/800x500?text=Project+Image';
          }}
        />
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <Title level={4} className="font-android !font-normal !text-white uppercase mb-2 flex-shrink-0">
          {getProjectDisplayName()}
        </Title>
        <Paragraph className="font-barlow text-orangicide !text-sm mb-4 flex-shrink-0">
          {getProjectInfo()}
        </Paragraph>
        <Paragraph className="font-barlow text-gray-300 mb-4 flex-1 overflow-hidden text-ellipsis line-clamp-4">
          {getProjectDescription()}
        </Paragraph>
        
        {/* Additional project info */}
        {/* <div className="flex justify-between items-center mb-4 text-sm">
          <span className="text-gray-400 font-barlow">
            Duration: <span className="text-white">{project.duration || 'N/A'}</span>
          </span>
          <div className="flex gap-4">
            <span className="text-gray-400 font-barlow">
              Year: <span className="text-white">{getProjectYear()}</span>
            </span>
            {project.budget && (
              <span className="text-orangicide font-barlow">
                {project.budget}
              </span>
            )}
          </div>
        </div> */}
        
        <div className="mt-auto flex-shrink-0">
          <a 
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/projects/${project.id}`); window.scrollTo(0, 0); }}
            className="text-orangicide font-barlow text-sm flex items-center w-fit"
          >
            View Project <img src="/assets/icons/carry-right.svg" alt="bullet" className="h-[10px] ml-1" />
          </a>
        </div>
      </div>
    </Card>
  );
}; 